import { createSharedComposable } from '@vueuse/core'
import debug from '../../utils/console'

declare global {
  interface Window {
    _taboola: Record<string, unknown>[]
  }
}

export type TaboolaEventDetail = {
  baseMode: string
  container: string
  itemCount: number
  placement: string
  variant: null
}
export type TaboolaEvent = {
  isTrusted: boolean
  bubbles: boolean
  cancelBubble: boolean
  cancelable: boolean
  composed: boolean
  currentTarget: null
  defaultPrevented: boolean
  detail: TaboolaEventDetail
  eventPhase: number
  returnValue: boolean
  srcElement: null
  target: null
  timeStamp: number
  type: string
}
const console = debug.withTag('useTaboolaAds')

const useTaboolaAds = createSharedComposable(() => {
  const taboolaPublisherName = useAppConfig().taboola.publisherName

  const { $assertive } = useNuxtApp()
  const tagType = $assertive?.analytics?.custom?.custom_1 || ''

  const isHeadLoadedPromise =
    getPageType() === 'homepage'
      ? loadScriptAsync(
          'head',
          {
            textContent: `window._taboola = window._taboola || [];
          _taboola.push({
            homepage:'auto',
            custom_tracking:'sub_id=${tagType}'
          });
          !function (e, f, u, i) { if (!document.getElementById(i)){ e.async = 1; e.src = u; e.id = i; f.parentNode.insertBefore(e, f); } }
            (document.createElement('script'), document.getElementsByTagName('script')[0], '//cdn.taboola.com/libtrc/${taboolaPublisherName}/loader.js', 'tb_loader_script');
            if(window.performance && typeof(window.performance.mark) == 'function') {window.performance.mark('tbl_ic');}`,
            key: 'taboola-homepage-header-script',
          },
          {
            id: 'taboola-homepage-header-script',
            type: 'text/javascript',
            referrerpolicy: 'no-referrer-when-downgrade',
            async: true,
            onload: () => console.debug('Taboola script loaded.'),
            onerror: () => console.error('Taboola script failed to load.'),
          }
        )
      : loadScriptAsync(
          'head',
          {
            textContent: `window._taboola = window._taboola || [];
          _taboola.push({
            article:'auto',
            custom_tracking:'sub_id=${tagType}'
          });
          !function (e, f, u, i) { if (!document.getElementById(i)){ e.async = 1; e.src = u; e.id = i; f.parentNode.insertBefore(e, f); } }
            (document.createElement('script'), document.getElementsByTagName('script')[0], '//cdn.taboola.com/libtrc/${taboolaPublisherName}/loader.js', 'tb_loader_script');
            if(window.performance && typeof(window.performance.mark) == 'function') {window.performance.mark('tbl_ic');}`,
            key: 'taboola-article-header-script',
          },
          {
            id: 'taboola-article-header-script',
            type: 'text/javascript',
            referrerpolicy: 'no-referrer-when-downgrade',
            async: true,
            onload: () => console.debug('Taboola script loaded.'),
            onerror: () => console.error('Taboola script failed to load.'),
          }
        )

  const isBodyLoadedPromise = loadScriptAsync(
    'body',
    {
      textContent: `window._taboola = window._taboola || [];
        _taboola.push({flush: true});`,
      key: 'taboola-homepage-body-script',
    },
    {
      id: 'taboola-homepage-body-script',
      type: 'text/javascript',
      async: true,
      onload: () => console.debug('Taboola body script loaded.'),
      onerror: () => console.error('Taboola body script failed to load.'),
    }
  )

  const triggerTaboolaRenderedEvent = async (
    mode: string,
    taboolaContainerName: string,
    taboolaPlacementName: string,
    callbackFn: () => void
  ): Promise<void> => {
    await isHeadLoadedPromise
    await isBodyLoadedPromise

    callbackFn()

    window._taboola = window._taboola || []
    window._taboola.push({
      mode: mode,
      container: taboolaContainerName,
      placement: taboolaPlacementName,
      target_type: 'mix',
    })
  }

  const fireRenderCallback = (
    placement: string,
    executed: Ref<boolean>,
    callbackFn: () => void
  ): Promise<void> => {
    return new Promise<void>((resolve) => {
      window._taboola.push({
        listenTo: 'render',
        handler: function (e: TaboolaEvent) {
          executed.value = true
          if (e.detail.placement === placement) {
            callbackFn()
            resolve()
          }
        },
      })
    })
  }

  const callRenderTaboolaWidgets = async () => {
    await isHeadLoadedPromise
    await isBodyLoadedPromise
    window._taboola = window._taboola || []
    window._taboola.push({ notify: 'newPageLoad' })
    window._taboola.push({ flush: true })
  }

  return {
    triggerTaboolaRenderedEvent,
    fireRenderCallback,
    callRenderTaboolaWidgets,
  }
})

export default useTaboolaAds
